import React from "react";
import { FaCheck, FaCrown } from "react-icons/fa";
import { PLAN_TYPES } from "../services/subscriptionService";
import { formatDateOnly } from "../../utils/date";

const LIGHT_CARD = "bg-gradient-to-br from-[#DDF3FB] to-[#D7F9E7] border border-[#1DE78B] shadow-[0_0_18px_rgba(29,231,139,0.35)] text-[#121212]";
const GRADIENT_CARD = "bg-gradient-to-br from-[#2FB6EA] to-[#3DE38A] text-white";

const yesNo = (enabled, yes, no = "Not available") => (enabled ? yes : no);

// Feature rows come from the plan's limits and flags, so they always match what the API enforces
export const planFeatures = (plan, planType) => {
  if (planType === PLAN_TYPES.VIEWER) {
    return [
      ["Live Streams", plan.private_live_enabled ? "Public and private streams" : "Public streams only"],
      ["Chat & Likes", "Included"],
      ["Replays & Past Lives", yesNo(plan.replay_enabled, "Included")],
      ["Ads", plan.ad_free_enabled ? "Ad-free viewing" : "Ads shown"],
    ];
  }
  return [
    ["GoLive Duration", `Up to ${plan.session_duration_minutes} Mins`],
    ["Live Sessions", plan.session_caps ? `${plan.session_caps} per billing period` : "Unlimited"],
    ["Viewer Limit", plan.audience_limit ? `Max ${plan.audience_limit} concurrent viewers` : "Unlimited"],
    ["Product Showcase", plan.product_selling_enabled ? "Allowed (seller account)" : null],
    ["Video Quality", plan.hd_streaming_enabled ? "HD video" : "Standard definition only"],
    ["Replays", yesNo(plan.replay_enabled, "Post-stream replays for viewers")],
    ["Monetization", yesNo(plan.donations_enabled, "Donations & gifts")],
    ["Private Stream", yesNo(plan.private_live_enabled, "Available")],
    ["Discoverability", plan.priority_discoverability ? "Priority boost" : "Basic only (no priority boost)"],
  ];
};

// What the "Your Plan" badge says about the subscription
export const planBadge = ({ subscription, current_plan: plan }) => {
  if (plan.is_free_tier) return "Free plan";
  if (subscription.status === "grace_period") return `Payment due by ${formatDateOnly(subscription.grace_period_end)}`;
  if (subscription.has_scheduled_change || !subscription.auto_renew) {
    return `Plan expires on ${formatDateOnly(subscription.current_billing_period_end)}`;
  }
  return `Renews on ${formatDateOnly(subscription.next_billing_date)}`;
};

/**
 * One plan with its features. `light` is the highlighted style (current plan, plan being bought);
 * `badge` replaces the action button (e.g. the expiry date on "Your Plan").
 */
const PlanCard = ({ plan, planType, light = false, actionLabel, onAction, actionDisabled = false, badge, className = "" }) => (
  <div className={`flex flex-col rounded-[28px] p-6 md:p-7 ${light ? LIGHT_CARD : GRADIENT_CARD} ${className}`}>
    <h3 className={`text-2xl md:text-[28px] font-bold leading-tight ${light ? "text-[#0B4F2A]" : "text-white"}`}>
      {plan.base_plan_name || plan.plan_name}
      {plan.description && <span className="block text-sm font-medium opacity-80 mt-1">{plan.description}</span>}
    </h3>

    {badge ? (
      <div className="mt-4 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#08AEEA] to-[#1DE78B] px-4 py-2.5 text-sm font-semibold text-[#0B2A1A]">
        <FaCrown className="text-lg" />
        {badge}
      </div>
    ) : (
      actionLabel && (
        <button
          type="button"
          onClick={onAction}
          disabled={actionDisabled}
          className={`mt-4 flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
            light ? "bg-gradient-to-r from-[#08AEEA] to-[#1DE78B] text-[#0B2A1A]" : "bg-white text-[#0B4F2A] hover:bg-[#EBF8ED]"
          }`}
        >
          <FaCrown className="text-lg" />
          {actionLabel}
        </button>
      )
    )}

    <p className={`mt-5 text-sm font-semibold italic ${light ? "text-[#0B4F2A]" : "text-white/90"}`}>Features</p>
    <div className={`mt-2 mb-3 h-0.5 w-2/3 ${light ? "bg-[#0B4F2A]" : "bg-white/50"}`} />

    <ul className="space-y-3">
      {planFeatures(plan, planType).map(([label, value]) => (
        <li key={label} className="flex items-start gap-3 text-sm">
          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${light ? "bg-[#0B4F2A]/15 text-[#0B4F2A]" : "bg-white/30 text-white"}`}>
            <FaCheck className="text-[10px]" />
          </span>
          <span>
            {label}:{" "}
            <span className="font-semibold">{value ?? <><span className="text-red-500">✕</span> Not allowed</>}</span>
          </span>
        </li>
      ))}
    </ul>
  </div>
);

export default PlanCard;
